import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AssignGroupToProjectParams,
  CLIUser,
  CreateCLIUserParams,
  CreateCLIUserResponse,
  CreateProjectParams,
  CreateProjectResponse,
  DeleteCLIUserParams,
  DeleteCLIUserResponse,
  DeleteProjectParams,
  GetCLIUserParams,
  GetCLIUserResponse,
  GetCLIUsersParams,
  GetCLIUsersResponse,
  GetOrganizationProjectsParams,
  GetOrganizationProjectsResponse,
  GetProjectParams,
  GetProjectResponse,
  Project,
  RemoveGroupFromProjectParams,
  TableProject,
  UpdateCLIUserParams,
  UpdateCLIUserResponse,
  UpdateProjectParams,
  UpdateProjectResponse
} from "./types";

export type ProjectsState = {
  organizationProjects: TableProject[] | null;
  areOrganizationProjectsLoading: boolean;
  project: Project | null;
  // areProjectsLoading: boolean;
  isProjectLoading: boolean;
  isProjectCreating: boolean;
  isProjectUpdating: boolean;
  isProjectDeleting: boolean;
  cliUsers: CLIUser[] | null;
  cliUser: CLIUser | null;
  isCLIUserLoading: boolean;
  areCLIUsersLoading: boolean;
  isCLIUserCreating: boolean;
  isCLIUserUpdating: boolean;
  isCLIUserDeleting: boolean;
  isGroupToProjectAssigning: boolean;
  isGroupFromProjectRemoving: boolean;
};

const initialState: ProjectsState = {
  organizationProjects: null,
  areOrganizationProjectsLoading: false,
  project: null,
  // areProjectsLoading: false,
  isProjectLoading: false,
  isProjectCreating: false,
  isProjectUpdating: false,
  isProjectDeleting: false,
  cliUsers: null,
  cliUser: null,
  isCLIUserLoading: false,
  areCLIUsersLoading: false,
  isCLIUserCreating: false,
  isCLIUserUpdating: false,
  isCLIUserDeleting: false,
  isGroupToProjectAssigning: false,
  isGroupFromProjectRemoving: false
};

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    // Get Project
    getProjectRequest: (state, action: PayloadAction<GetProjectParams>) => {
      state.isProjectLoading = true;
    },
    getProjectSuccess: (state, action: PayloadAction<GetProjectResponse>) => {
      state.project = action.payload;
      state.isProjectLoading = false;
    },
    getProjectFailure: (
      state,
      action: PayloadAction<{ params: GetProjectParams; error: string }>
    ) => {
      state.isProjectLoading = false;
    },

    // Get Organization Projects
    getOrganizationProjectsRequest: (
      state,
      action: PayloadAction<GetOrganizationProjectsParams>
    ) => {
      state.areOrganizationProjectsLoading = true;
    },
    getOrganizationProjectsSuccess: (
      state,
      action: PayloadAction<GetOrganizationProjectsResponse>
    ) => {
      state.organizationProjects = action.payload;
      state.areOrganizationProjectsLoading = false;
    },
    getOrganizationProjectsFailure: (
      state,
      action: PayloadAction<{
        params: GetOrganizationProjectsParams;
        error: string;
      }>
    ) => {
      state.areOrganizationProjectsLoading = false;
    },

    // Create Project
    createProjectRequest: (
      state,
      action: PayloadAction<CreateProjectParams>
    ) => {
      state.isProjectCreating = true;
    },
    createProjectSuccess: (
      state,
      action: PayloadAction<CreateProjectResponse>
    ) => {
      state.isProjectCreating = false;
    },
    createProjectFailure: (
      state,
      action: PayloadAction<{ params: CreateProjectParams; error: string }>
    ) => {
      state.isProjectCreating = false;
    },

    // Update Project
    updateProjectRequest: (
      state,
      action: PayloadAction<UpdateProjectParams>
    ) => {
      state.isProjectUpdating = true;
    },
    updateProjectSuccess: (
      state,
      action: PayloadAction<UpdateProjectResponse>
    ) => {
      state.isProjectUpdating = false;
    },
    updateProjectFailure: (
      state,
      action: PayloadAction<{ params: UpdateProjectParams; error: string }>
    ) => {
      state.isProjectUpdating = false;
    },

    // Delete Project
    deleteProjectRequest: (
      state,
      action: PayloadAction<DeleteProjectParams>
    ) => {
      state.isProjectDeleting = true;
    },
    deleteProjectSuccess: (state) => {
      state.isProjectDeleting = false;
      state.project = null;
    },
    deleteProjectFailure: (
      state,
      action: PayloadAction<{ params: DeleteProjectParams; error: string }>
    ) => {
      state.isProjectDeleting = false;
    },

    // Get CLI User
    getCLIUserRequest: (state, action: PayloadAction<GetCLIUserParams>) => {
      state.isCLIUserLoading = true;
    },
    getCLIUserSuccess: (state, action: PayloadAction<GetCLIUserResponse>) => {
      state.cliUser = action.payload;
      state.isCLIUserLoading = false;
    },
    getCLIUserFailure: (
      state,
      action: PayloadAction<{ params: GetCLIUserParams; error: string }>
    ) => {
      state.isCLIUserLoading = false;
    },

    // Get CLI Users
    getCLIUsersRequest: (state, action: PayloadAction<GetCLIUsersParams>) => {
      state.areCLIUsersLoading = true;
    },
    getCLIUsersSuccess: (state, action: PayloadAction<GetCLIUsersResponse>) => {
      state.cliUsers = action.payload;
      state.areCLIUsersLoading = false;
    },
    getCLIUsersFailure: (
      state,
      action: PayloadAction<{ params: GetCLIUsersParams; error: string }>
    ) => {
      state.areCLIUsersLoading = false;
    },

    // Create CLI User
    createCLIUserRequest: (
      state,
      action: PayloadAction<CreateCLIUserParams>
    ) => {
      state.isCLIUserCreating = true;
    },
    createCLIUserSuccess: (
      state,
      action: PayloadAction<CreateCLIUserResponse>
    ) => {
      state.isCLIUserCreating = false;
    },
    createCLIUserFailure: (
      state,
      action: PayloadAction<{ params: CreateCLIUserParams; error: string }>
    ) => {
      state.isCLIUserCreating = false;
    },

    // Update CLI User
    updateCLIUserRequest: (
      state,
      action: PayloadAction<UpdateCLIUserParams>
    ) => {
      state.isCLIUserUpdating = true;
    },
    updateCLIUserSuccess: (
      state,
      action: PayloadAction<UpdateCLIUserResponse>
    ) => {
      state.isCLIUserUpdating = false;
    },
    updateCLIUserFailure: (
      state,
      action: PayloadAction<{ params: UpdateCLIUserParams; error: string }>
    ) => {
      state.isCLIUserUpdating = false;
    },

    // Delete CLI User
    deleteCLIUserRequest: (
      state,
      action: PayloadAction<DeleteCLIUserParams>
    ) => {
      state.isCLIUserDeleting = true;
    },
    deleteCLIUserSuccess: (
      state,
      action: PayloadAction<DeleteCLIUserResponse>
    ) => {
      state.isCLIUserDeleting = false;
    },
    deleteCLIUserFailure: (
      state,
      action: PayloadAction<{ params: DeleteCLIUserParams; error: string }>
    ) => {
      state.isCLIUserDeleting = false;
    },

    // Clear CLI Users
    clearCLIUsers: (state) => {
      state.cliUsers = null;
    },

    // Assign Group To Project
    assignGroupToProjectRequest: (
      state,
      action: PayloadAction<AssignGroupToProjectParams>
    ) => {
      state.isGroupToProjectAssigning = true;
    },
    assignGroupToProjectSuccess: (state) => {
      state.isGroupToProjectAssigning = false;
    },
    assignGroupToProjectFailure: (
      state,
      action: PayloadAction<{
        params: AssignGroupToProjectParams;
        error: string;
      }>
    ) => {
      state.isGroupToProjectAssigning = false;
    },

    // Remove Group From Project
    removeGroupFromProjectRequest: (
      state,
      action: PayloadAction<RemoveGroupFromProjectParams>
    ) => {
      state.isGroupFromProjectRemoving = true;
    },
    removeGroupFromProjectSuccess: (state) => {
      state.isGroupFromProjectRemoving = false;
    },
    removeGroupFromProjectFailure: (
      state,
      action: PayloadAction<{
        params: RemoveGroupFromProjectParams;
        error: string;
      }>
    ) => {
      state.isGroupFromProjectRemoving = false;
    },

    // Clear
    clear: () => initialState
  }
});

export const {
  getProjectRequest,
  getProjectSuccess,
  getProjectFailure,
  getOrganizationProjectsRequest,
  getOrganizationProjectsSuccess,
  getOrganizationProjectsFailure,
  createProjectRequest,
  createProjectSuccess,
  createProjectFailure,
  updateProjectRequest,
  updateProjectSuccess,
  updateProjectFailure,
  deleteProjectRequest,
  deleteProjectSuccess,
  deleteProjectFailure,
  getCLIUserRequest,
  getCLIUserSuccess,
  getCLIUserFailure,
  getCLIUsersRequest,
  getCLIUsersSuccess,
  getCLIUsersFailure,
  createCLIUserRequest,
  createCLIUserSuccess,
  createCLIUserFailure,
  updateCLIUserRequest,
  updateCLIUserSuccess,
  updateCLIUserFailure,
  deleteCLIUserRequest,
  deleteCLIUserSuccess,
  deleteCLIUserFailure,
  clearCLIUsers,
  assignGroupToProjectRequest,
  assignGroupToProjectSuccess,
  assignGroupToProjectFailure,
  removeGroupFromProjectRequest,
  removeGroupFromProjectSuccess,
  removeGroupFromProjectFailure,
  clear
} = projectsSlice.actions;

export const { actions: projectsActions } = projectsSlice;

export const reducer = projectsSlice.reducer;
